import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AuthFrame from "./AuthFrame";
import { TitleBlock, Field, Input, PasswordInput, Note, ErrorNote, Actions, PrimaryBtn } from "./pieces";
import { readSignup, writeSignup } from "./signupState";
import { registerFromSignup } from "./signupFlow";
import { accountTypeOf, isAccountType } from "./accountTypes";

// The server refuses a first or last name over 60 characters.
export const NAME_MAX = 60;

const NAME_INPUT = "!w-[166px] max-sm:!w-auto max-sm:min-w-0 max-sm:flex-1";

// 04 · Name and email, plus the password that email login needs (Google
// sign-ups get none), and for a rep the agency. Athletes go on to the
// birthday step, which creates their account; every other type's account
// is created here, then onboarding (coaches) or the home opens.
const SignupNamePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Read once, when the page opens, so the guard below only ever sees the
  // state the page started with (creating the account clears it).
  const [saved] = useState(readSignup);
  const type = accountTypeOf(saved.accountType);
  const athlete = type.key === "IS_ATHLETE";
  const rep = type.key === "IS_REP";
  const [first, setFirst] = useState(saved.firstName || "");
  const [last, setLast] = useState(saved.lastName || "");
  const [password, setPassword] = useState(saved.password || "");
  const [agency, setAgency] = useState(saved.agency || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!saved.ticket) navigate("/signup/email", { replace: true });
    else if (!isAccountType(saved.accountType)) navigate("/register", { replace: true });
  }, [saved, navigate]);

  // The password never goes into the tab's storage: the account is made now.
  const create = async (details) => {
    writeSignup(details);
    setBusy(true);
    try {
      const problem = await registerFromSignup({
        identity: { ...saved, ...details, password },
        roleKey: type.key,
        extra: rep ? { agency: details.agency } : {},
        dispatch,
        navigate,
      });
      if (problem) setError(problem.message);
    } catch {
      setError("We could not reach CACHE. Try again.");
    } finally { setBusy(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (!first.trim() || !last.trim()) { setError("Enter your first and last name."); return; }
    if (first.trim().length > NAME_MAX || last.trim().length > NAME_MAX) { setError(`Keep names to ${NAME_MAX} characters or fewer.`); return; }
    if (!saved.viaGoogle && password.length < 8) { setError("Use at least 8 characters for your password."); return; }
    const names = { firstName: first.trim(), lastName: last.trim() };
    if (!athlete) { create(rep ? { ...names, agency: agency.trim() } : names); return; }
    writeSignup({ ...names, password: saved.viaGoogle ? "" : password });
    navigate("/signup/birthday");
  };

  // Back goes to the type screen, which shows the confirmed email with
  // Start over. The code and email screens forward a confirmed ticket
  // straight back here, so Back to them would do nothing. Athletes and
  // coaches see their progress; the other types finish on this step. On
  // phones the body starts 16px under the step bars (Figma 04).
  return (
    <AuthFrame
      progress={type.onboarding ? { step: 1, total: 4 } : undefined}
      phoneTop={type.onboarding ? "steps" : undefined}
      title="Create your account"
      onBack={() => navigate("/register")}
    >
      <form className="contents" onSubmit={submit}>
        <TitleBlock
          eyebrow="About you"
          title={saved.viaGoogle ? "Confirm your name" : "What is your name?"}
          sub={saved.viaGoogle ? "We took this from your Google account. Fix anything that is not right." : "We keep first and last name separate so your profile is not built from a social nickname."}
        />
        <Field label="First and last name">
          {/* 166px each from 640px up. On phones the two share the row
              (Figma 1759:1326: flex-1, 10px apart), so they fit any column
              from 272px (a 320px screen) up. */}
          <div className="flex items-start gap-[10px]">
            <Input className={NAME_INPUT} autoComplete="given-name" placeholder="First name" maxLength={NAME_MAX} value={first} onChange={(e) => setFirst(e.target.value)} aria-label="First name" />
            <Input className={NAME_INPUT} autoComplete="family-name" placeholder="Last name" maxLength={NAME_MAX} value={last} onChange={(e) => setLast(e.target.value)} aria-label="Last name" />
          </div>
        </Field>
        <Field label="Email">
          <Input value={saved.email || ""} readOnly aria-readonly="true" aria-label="Email" />
        </Field>
        {!saved.viaGoogle && (
          <Field label="Create a password">
            <PasswordInput autoComplete="new-password" placeholder="At least 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Create a password" />
          </Field>
        )}
        {rep && (
          <Field label="Agency · optional">
            <Input autoComplete="organization" placeholder="Agency or company name" value={agency} onChange={(e) => setAgency(e.target.value)} aria-label="Agency" />
          </Field>
        )}
        {type.nameNote && <Note>{type.nameNote}</Note>}
        {error && <ErrorNote error={error} onLogin={() => navigate("/login")} />}
        <Actions>
          <PrimaryBtn type="submit" loading={busy}>Continue</PrimaryBtn>
        </Actions>
      </form>
    </AuthFrame>
  );
};

export default SignupNamePage;
